import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveExplorerOnboarding } from "../../api/explorerOnboardingApi";
import { useTranslation } from "react-i18next";
import { isLoggedIn } from "../../auth/authStorage";
import {
  clearExplorerPostMembershipEventCombo,
  clearExplorerPendingOnboarding,
  getExplorerEventId,
  getExplorerFlow,
  getExplorerLeadProfile,
  getExplorerLocalProfile,
  getExplorerPendingOnboarding,
  getExplorerStep2Persisted,
  EXPLORER_EVENT_PAYMENT_DONE_KEY,
  EXPLORER_PENDING_SUCCESS_KEY,
  REGISTRATION_SHOW_CONFIRMATION_KEY,
  setExplorerPostMembershipEventCombo,
  notifyExplorerComboRegisterChromeChanged,
} from "../../explorer/explorerSession";
import { persistExplorerProfileFromSession, explorerSignupPath, ensureExplorerProfileSyncedForEventBooking } from "../../explorer/explorerProfileSync";
import ExplorerWizardShell from "./ExplorerWizardShell";

/**
 * Final explorer step — two equal cards: membership + discounted booking vs full-price event booking.
 */
function ExplorerCheckout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const eventId = useMemo(() => getExplorerEventId(), []);
  const flow = useMemo(() => getExplorerFlow(), []);
  const [syncing, setSyncing] = useState(true);
  const [syncError, setSyncError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setSyncError(null);
      setSyncing(true);
      const local = getExplorerLocalProfile();
      const pending = getExplorerPendingOnboarding();
      const step2Done = getExplorerStep2Persisted();
      const eventIdNum = eventId ? parseInt(eventId, 10) : NaN;
      const safeEventId = Number.isFinite(eventIdNum) ? eventIdNum : undefined;

      try {
        if (isLoggedIn() && pending?.segment) {
          const local = getExplorerLocalProfile();
          if (local) {
            await persistExplorerProfileFromSession({
              segment: pending.segment,
              discoveryAnswers: pending.discoveryAnswers,
            });
          }
          await saveExplorerOnboarding({
            segment: pending.segment,
            flowType: flow,
            discoveryAnswers: pending.discoveryAnswers,
            eventId: safeEventId,
          });
          clearExplorerPendingOnboarding();
          return;
        }

        if (!isLoggedIn()) {
          if (!local) {
            if (!cancelled) {
              navigate(explorerSignupPath(), { replace: true });
            }
            return;
          }
          if (!local.firstName?.trim() || !local.lastName?.trim()) {
            if (!cancelled) {
              navigate(explorerSignupPath(), { replace: true });
            }
            return;
          }
          if (!step2Done) {
            await persistExplorerProfileFromSession();
          }
        } else {
          const localProfile = getExplorerLocalProfile();
          if (
            localProfile &&
            (!localProfile.firstName?.trim() || !localProfile.lastName?.trim())
          ) {
            if (!cancelled) {
              navigate(explorerSignupPath(), { replace: true });
            }
            return;
          }
        }
      } catch (err: unknown) {
        let message = t("explorer.checkout.syncFailed");
        if (err && typeof err === "object" && "response" in err) {
          const data = (err as { response?: { data?: { error?: unknown } } }).response?.data;
          if (data?.error && typeof data.error === "string") message = data.error;
        }
        if (!cancelled) setSyncError(message);
      } finally {
        if (!cancelled) setSyncing(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [eventId, flow, navigate, t]);

  const goEventOnly = () => {
    const afterLoginPath = eventId
      ? `/confirm-booking/${eventId}`
      : "/event-booking";
    if (!isLoggedIn()) {
      const q = new URLSearchParams();
      q.set("redirect", afterLoginPath);
      q.set("explorer", "1");
      q.set("mode", "register");
      const lead = getExplorerLeadProfile();
      if (lead?.email) q.set("email", lead.email);
      if (lead?.firstName) q.set("prefillFirstName", lead.firstName);
      if (lead?.lastName) q.set("prefillLastName", lead.lastName);
      navigate(`/?${q.toString()}`, { replace: true });
      return;
    }
    void ensureExplorerProfileSyncedForEventBooking().catch(() => {
      /* best-effort; do not block booking for legacy users missing names */
    });
    if (eventId) {
      sessionStorage.setItem(EXPLORER_PENDING_SUCCESS_KEY, "1");
    }
    navigate(afterLoginPath, { replace: true });
  };

  const goMembershipFirst = () => {
    const leadEmail = getExplorerLeadProfile()?.email?.trim();
    const authEmail = localStorage.getItem("auth_email")?.trim();
    const ownerEmail = leadEmail || authEmail;
    if (eventId?.trim()) {
      setExplorerPostMembershipEventCombo(eventId, ownerEmail);
    } else {
      clearExplorerPostMembershipEventCombo();
    }
    sessionStorage.removeItem(EXPLORER_EVENT_PAYMENT_DONE_KEY);
    sessionStorage.removeItem(REGISTRATION_SHOW_CONFIRMATION_KEY);
    notifyExplorerComboRegisterChromeChanged();
    if (!isLoggedIn()) {
      const q = new URLSearchParams();
      q.set("redirect", "/register-user");
      q.set("explorer", "1");
      q.set("mode", "register");
      const lead = getExplorerLeadProfile();
      if (lead?.email) q.set("email", lead.email);
      if (lead?.firstName) q.set("prefillFirstName", lead.firstName);
      if (lead?.lastName) q.set("prefillLastName", lead.lastName);
      navigate(`/?${q.toString()}`, { replace: true });
      return;
    }
    void ensureExplorerProfileSyncedForEventBooking()
      .catch(() => {
        /* best-effort before membership checkout */
      })
      .finally(() => {
        navigate("/register-user", { replace: true });
      });
  };

  return (
    <ExplorerWizardShell
      step={3}
      totalSteps={3}
      progressLabel={t("explorer.checkout.progressLabel")}
      checkoutDesign
      footerPlacement="below"
      onBack={() => navigate("/explorer/segmentation", { replace: true })}
      error={syncError}
    >
      <h1 className="explorer-wizard-title explorer-wizard-title--checkout">
        Step 3: {t("explorer.checkout.title")}
      </h1>
      <div className="explorer-wizard-choice-grid explorer-wizard-choice-grid--checkout explorer-wizard-choice-grid--two-col mt-2">
        {/* Left: membership → book with discount / member pricing */}
        <div
          className="explorer-wizard-choice-card explorer-wizard-choice-card--checkout explorer-wizard-choice-card--membership"
          role="group"
          aria-label={t("explorer.checkout.membershipAria")}
        >
          <span className="explorer-wizard-choice-card__badge explorer-wizard-choice-card__badge--membership">
            {t("explorer.checkout.membershipBadge")}
          </span>
          <h3 className="explorer-wizard-choice-card__title">
            {t("explorer.checkout.membershipTitle")}
          </h3>
          <p className="explorer-wizard-choice-card__desc">
            {t("explorer.checkout.membershipDesc")}
          </p>
          <button
            type="button"
            className="btn explorer-wizard-btn explorer-wizard-btn--checkout-membership-cta w-100"
            onClick={goMembershipFirst}
            disabled={syncing}
          >
            {t("explorer.checkout.membershipCta")}
          </button>
        </div>

        {/* Right: full payment, no member discount */}
        <div
          className="explorer-wizard-choice-card explorer-wizard-choice-card--checkout explorer-wizard-choice-card--ticket"
          role="group"
          aria-label={t("explorer.checkout.ticketAria")}
        >
          <span className="explorer-wizard-choice-card__badge explorer-wizard-choice-card__badge--ticket">
            {t("explorer.checkout.ticketBadge")}
          </span>
          <h3 className="explorer-wizard-choice-card__title">
            {t("explorer.checkout.ticketTitle")}
          </h3>
          <p className="explorer-wizard-choice-card__desc">
            {t("explorer.checkout.ticketDesc")}
          </p>
          <button
            type="button"
            className="btn explorer-wizard-btn explorer-wizard-btn--checkout-primary w-100"
            onClick={goEventOnly}
            disabled={syncing}
          >
            {t("explorer.checkout.ticketCta")}
          </button>
        </div>
      </div>
    </ExplorerWizardShell>
  );
}

export default ExplorerCheckout;
